/**
 * Sets up the workspace a new user owns.
 *
 * Runs straight after `fetchAccount`, because the name on the workspace comes
 * from whoami rather than from anything the sign-up screens collected.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import { normalizeReferralCode } from "./referralCode";
import type { GetToken } from "../types/auth.types";
import type {
  CreateWorkspaceInput,
  CreateWorkspacePayload,
  CreateWorkspaceResponse,
  MembershipResponse,
  WorkspaceMembership,
  WorkspaceReferral,
  WorkspaceReferralResponse,
} from "../types/onboarding.types";

/**
 * The part of the payload no screen collects yet. Fixed here rather than
 * inlined at the call site, so the onboarding form that eventually asks for
 * these has one place to replace.
 */
export const WORKSPACE_DEFAULTS = {
  currency: "INR",
  timezone: "Asia/Kolkata",
  /** April - the Indian financial year. */
  financialYearStartMonth: 4,
} as const;

/**
 * The partner behind the practice, or `null`.
 *
 * A referral with no code is not a referral - the code is the whole of what
 * was recorded - so it reads as absent rather than as a partner nobody can
 * name. The partner object itself may legitimately be thin: the names are for
 * display, and a blank one is a blank line on a screen, not a missing record.
 */
const toReferral = (
  response: WorkspaceReferralResponse | null | undefined
): WorkspaceReferral | null => {
  const referralCode = response?.referral_code?.trim();
  if (!referralCode) return null;

  return {
    referralCode,
    partnerName: response?.partner?.name?.trim() ?? "",
    partnerBrandName: response?.partner?.brand_name?.trim() ?? "",
  };
};

const toMembership = (response: MembershipResponse): WorkspaceMembership => ({
  role: response.role,
  workspace: {
    publicId: response.workspace.public_id,
    name: response.workspace.name,
    slug: response.workspace.slug,
    timezone: response.workspace.timezone,
    currency: response.workspace.currency,
    financialYearStartMonth: response.workspace.financial_year_start_month,
    referral: toReferral(response.workspace.referral),
  },
});

/**
 * Every membership the signed-in user holds.
 *
 * `GET /api/v1/onboarding/workspace/`. An empty list is the canonical answer
 * for a user who has not onboarded yet - a `200` and not a `404` - so an empty
 * array here is an ordinary state rather than a failure to report.
 *
 * What the Billing screens come here for is `workspace.referral`: the code the
 * practice arrived through, which the coupon lookup needs and which no other
 * endpoint publishes. See `useWorkspaceReferral`.
 */
export async function fetchMemberships(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<WorkspaceMembership[]> {
  const response = await api.get<CreateWorkspaceResponse | null>(
    API_ENDPOINTS.onboarding.workspace,
    { token: await getToken(), signal }
  );

  return (response?.memberships ?? []).map(toMembership);
}

/**
 * Creates the workspace for the signed-in user and returns every membership
 * they hold afterwards - the new one included.
 *
 * Needs an active session, like everything else behind a bearer token.
 *
 * ## The referral code
 *
 * `referral_code` goes up where the visitor arrived through a partner's link,
 * and this is the only call in the app that can record one: the backend reads
 * it when the practice is created and at no other moment. A repeat POST is
 * idempotent - `200` with the stored workspace rather than `201` - and a code
 * sent on one is ignored, so this never has to work out whether it is the
 * first attempt.
 *
 * The field is left off the body entirely for an unreferred practice rather
 * than sent empty. A blank string is accepted by the backend, but "no key" is
 * the honest way to say a thing did not happen, and it keeps the body of the
 * ordinary sign-up free of a field that has nothing to do with it.
 *
 * Nothing here treats an unknown code as a problem. The backend ignores one
 * and creates the practice anyway - deliberately, so that a mistyped or
 * expired link can never cost somebody their account - and this call has no
 * better judgement to apply than that.
 */
export async function createWorkspace(
  getToken: GetToken,
  input: CreateWorkspaceInput,
  signal?: AbortSignal
): Promise<WorkspaceMembership[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot create a workspace.");
  }

  const referralCode = normalizeReferralCode(input.referralCode);

  const payload: CreateWorkspacePayload = {
    practice_name: input.practiceName,
    currency: WORKSPACE_DEFAULTS.currency,
    timezone: WORKSPACE_DEFAULTS.timezone,
    financial_year_start_month: WORKSPACE_DEFAULTS.financialYearStartMonth,
    full_name: input.fullName,
    ...(referralCode ? { referral_code: referralCode } : {}),
  };

  const response = await api.post<CreateWorkspaceResponse>(
    API_ENDPOINTS.onboarding.workspace,
    payload,
    { token, signal }
  );

  return (response.memberships ?? []).map(toMembership);
}
